// Decorative stickers scattered down the empty space either side of the flow.
//
// Built once at startup and parked behind everything, deliberately outside the
// render cycle — rebuilding them on every state change would make them flicker
// and reshuffle every time she tapped something.
//
// Each load scatters them differently. The randomness is drawn ONCE, when the
// layer is built, and stored on the elements — layoutStickers runs after every
// render, so drawing fresh numbers there would make them leap about every time
// she tapped something. New arrangement per visit, still throughout a visit.
//
// Nothing here is interactive.

const STICKERS = [
  // Ordered, not just listed: buildStickerLayer takes the first `count` of
  // these, so the ones at the top are the ones that appear. The rest stay
  // available — raising the count brings them back with no other change.
  { name: 'duo', scale: 1.343 },
  { name: 'kuromi', scale: 1.004 },
  { name: 'appledog-bunny', scale: 1.061 },
  { name: 'pink-bear', scale: 0.75 },
  { name: 'pizza-mouse', scale: 1.5 },
  { name: 'bunny-balloon', scale: 1.5 },
  { name: 'blue-cat', scale: 0.927 },
  { name: 'monkey-banana', scale: 1.419 },
  { name: 'sleepy-blue', scale: 0.836 },
  { name: 'pink-fluff', scale: 0.922 },
  { name: 'bread-cat', scale: 1.0 },
  { name: 'pig-glasses', scale: 0.76 },
];

/** How many of them sit in the empty space below the flow rather than beside it. */
const BENEATH = 3;

/** Draw and remember a per-sticker random, so it survives re-layout. */
function roll(sticker, key) {
  if (sticker.dataset[key] === undefined) sticker.dataset[key] = String(Math.random());
  return Number(sticker.dataset[key]);
}

/**
 * Build the pool. Positions are not decided here — see layoutStickers, which
 * needs to measure the page and therefore has to run after a render.
 *
 * `count` is a ceiling, not a target: how many actually appear depends on how
 * much empty space there is, so a long scrolling page gets more of them and a
 * single card gets few. There are only twelve images, so a full page repeats
 * some — spread far apart down a tall page, that reads as a scatter rather
 * than as duplication.
 *
 * @param {object} [options]
 * @param {number} [options.count] the most that could ever be shown
 * @returns {HTMLElement} the layer, ready to append to <body>
 */
export function buildStickerLayer({ count = 40 } = {}) {
  const layer = document.createElement('div');
  layer.className = 'stickers';
  layer.setAttribute('aria-hidden', 'true');

  for (let i = 0; i < count; i += 1) {
    const entry = STICKERS[i % STICKERS.length];
    const sticker = document.createElement('img');
    sticker.className = 'sticker';
    sticker.src = `./stickers/${entry.name}.png`;
    sticker.dataset.name = entry.name;
    sticker.style.setProperty('--scale', String(entry.scale));
    sticker.alt = '';
    sticker.loading = 'lazy';
    sticker.decoding = 'async';
    sticker.style.setProperty('--tilt', `${(Math.random() - 0.5) * 44}deg`);
    sticker.style.setProperty('--bob-delay', `${Math.random() * 6}s`);
    sticker.style.setProperty('--bob-time', `${5 + Math.random() * 5}s`);
    sticker.dataset.depth = String(0.12 + Math.random() * 0.3);
    // Drawn now, reused by every later layout.
    roll(sticker, 'jx'); roll(sticker, 'jy'); roll(sticker, 'pick');
    layer.append(sticker);
  }

  return layer;
}

/**
 * Choose which items to show, so that every distinct key appears at least once
 * before any key appears twice.
 *
 * Taking the first N of a randomly ordered pool does not do this: with forty
 * candidates cycling twelve images and eighteen slots, a random subset can
 * easily use one image three times and leave another out entirely. Dealing one
 * full round of distinct keys first guarantees a complete set, and only then
 * fills the remaining slots.
 *
 * @param {{key: string, pick: number}[]} items ordered; `pick` is the shuffle
 * @param {number} shown how many slots there are
 * @returns {{key: string, pick: number}[]}
 */
export function selectForDisplay(items, shown) {
  /** @type {Map<string, object[]>} */
  const byKey = new Map();
  for (const item of items) {
    byKey.set(item.key, [...(byKey.get(item.key) ?? []), item]);
  }

  const rounds = [];
  let more = true;
  while (more) {
    more = false;
    const round = [];
    for (const queue of byKey.values()) {
      const next = queue.shift();
      if (next) { round.push(next); more = true; }
    }
    // Shuffle within the round so it is not the same order every time, while
    // the round boundary still guarantees completeness.
    round.sort((a, b) => a.pick - b.pick);
    rounds.push(...round);
  }
  return rounds.slice(0, shown);
}

/**
 * Decide which item goes in which cell, keeping copies of the same image apart.
 *
 * There are only twelve images and up to forty slots, so a full page repeats
 * some. Placed naively two of the same can land side by side, which reads as a
 * mistake rather than a scatter.
 *
 * Each cell takes whichever remaining item sits furthest from the nearest copy
 * of its own key. Greedy, but it only has to beat "adjacent", and it degrades
 * gracefully: once every key is on the board it simply prefers the most distant
 * option rather than failing. `pick` breaks ties so the result still varies.
 *
 * @param {{key: string, pick: number}[]} items
 * @param {{c: number, r: number}[]} cells  same length as items
 * @returns {{item: object, cell: {c: number, r: number}}[]}
 */
export function assignToCells(items, cells) {
  const remaining = [...items];
  /** @type {Map<string, {c: number, r: number}[]>} */
  const placed = new Map();
  const out = [];

  for (const cell of cells) {
    if (remaining.length === 0) break;
    let best = 0;
    let bestScore = -Infinity;
    for (let n = 0; n < remaining.length; n += 1) {
      const others = placed.get(remaining[n].key) ?? [];
      let nearest = Infinity;
      for (const other of others) {
        nearest = Math.min(nearest, Math.hypot(other.c - cell.c, other.r - cell.r));
      }
      // Capped, so an unused key always wins outright, and among used ones the
      // furthest is preferred.
      const score = Math.min(nearest, 6) - remaining[n].pick * 0.01;
      if (score > bestScore) { bestScore = score; best = n; }
    }
    const [item] = remaining.splice(best, 1);
    placed.set(item.key, [...(placed.get(item.key) ?? []), cell]);
    out.push({ item, cell });
  }
  return out;
}

/**
 * Place them evenly across whatever space the content is not using.
 *
 * The shape of that space changes completely between screens — the flow is a
 * tall narrow column with two side bands, the asking is one small card with
 * empty page all around it — so positions cannot be fixed at build time. This
 * measures the content, lays a grid over the page, discards the cells the
 * content occupies, and spreads the stickers across what is left.
 *
 * @param {HTMLElement} layer
 * @param {HTMLElement} content the element to place around
 */
export function layoutStickers(layer, content) {
  const stickers = [...layer.querySelectorAll('.sticker')];
  if (stickers.length === 0) return;

  const pageHeight = Math.max(document.documentElement.scrollHeight, innerHeight);
  const pageWidth = innerWidth;

  // Clearance kept around every sticker: its own box plus room for the bob and
  // the parallax drift, which shift it a few pixels after placement.
  const EDGE = 12;

  // The content's footprint, as a fraction of the page. Measured from its
  // children rather than the container: the container carries page padding
  // that is empty space we want to use.
  const boxes = [...content.children].map((child) => child.getBoundingClientRect());
  const avoid = boxes.length
    ? {
        left: Math.min(...boxes.map((b) => b.left)) / pageWidth,
        right: Math.max(...boxes.map((b) => b.right)) / pageWidth,
        top: (Math.min(...boxes.map((b) => b.top)) + window.scrollY) / pageHeight,
        bottom: (Math.max(...boxes.map((b) => b.bottom)) + window.scrollY) / pageHeight,
      }
    : { left: 1, right: 1, top: 1, bottom: 1 };

  // A little clearance so nothing crowds the panels.
  const pad = 0.02;
  avoid.left -= pad; avoid.right += pad; avoid.top -= pad; avoid.bottom += pad;

  // The same region in pixels. Cell-centre tests are not enough on their own:
  // a cell centre can sit outside the content while the sticker placed in it —
  // jittered, and up to 114px wide — still laps over the panel. Every final
  // position is checked against this box.
  const block = {
    left: avoid.left * pageWidth,
    right: avoid.right * pageWidth,
    top: avoid.top * pageHeight,
    bottom: avoid.bottom * pageHeight,
  };
  const overlaps = (x, y, w, h) =>
    x < block.right && x + w > block.left && y < block.bottom && y + h > block.top;

  // Cells are kept roughly square, so a tall page gets proportionally more
  // rows and therefore more stickers — which is what stops a long scroll
  // feeling empty.
  const COLS = pageWidth < 640 ? 3 : 5;
  const ROWS = Math.max(3, Math.round((pageHeight / pageWidth) * COLS * 1.15));

  const free = [];
  for (let r = 0; r < ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      const cx = (c + 0.5) / COLS;
      const cy = (r + 0.5) / ROWS;
      const clash = cx > avoid.left && cx < avoid.right && cy > avoid.top && cy < avoid.bottom;
      if (!clash) free.push({ c, r });
    }
  }
  // On a phone the content fills almost the whole width, so a long page has no
  // free cells at all — every column overlaps it. Rather than showing nothing,
  // fall back to tucking them against the edges, where they peek out from
  // behind the panels. This is the only case where they sit over the content.
  if (free.length < 4) {
    const tuck = Math.min(stickers.length, Math.max(4, Math.round(pageHeight / 420)));
    stickers.forEach((sticker, i) => {
      if (i >= tuck) { sticker.hidden = true; return; }
      sticker.hidden = false;
      const w = sticker.offsetWidth || 60;
      const h = sticker.offsetHeight || 60;
      const onLeft = i % 2 === 0;
      const y = ((i + 0.5) / tuck) * pageHeight + (roll(sticker, 'jy') - 0.5) * 60;
      sticker.style.top =
        `${((Math.max(EDGE, Math.min(pageHeight - h - EDGE, y)) / pageHeight) * 100).toFixed(2)}%`;
      sticker.style.left = onLeft ? `${(-w * 0.35 / pageWidth * 100).toFixed(2)}%` : '';
      sticker.style.right = onLeft ? '' : `${(-w * 0.35 / pageWidth * 100).toFixed(2)}%`;
    });
    return;
  }

  // Spread across the free cells rather than taking the first N, so they do
  // not bunch at the top when there is plenty of room.
  //
  // Clamping is done in pixels against each sticker's own box. Clamping the
  // top-left corner to a percentage does not work: the corner can sit inside
  // the page while the sticker itself hangs over the edge, and they vary in
  // size by up to 1.5x.

  // One per free cell. Any beyond that are stood down rather than doubled up.
  const pool = stickers.map((sticker) => ({
    sticker,
    key: sticker.dataset.name,
    pick: roll(sticker, 'pick'),
  }));
  const shown = Math.min(pool.length, free.length);

  // Every image appears at least once before any appears twice.
  const remaining = selectForDisplay(pool, shown);
  const showing = new Set(remaining.map((entry) => entry.sticker));
  for (const { sticker } of pool) if (!showing.has(sticker)) sticker.hidden = true;

  // Which cells to use: spread across the free ones rather than taking the
  // first N, so they do not bunch when there is more room than stickers.
  const cellsToFill = Array.from({ length: shown },
    (_, slot) => free[Math.floor((slot * free.length) / shown) % free.length]);

  const chosen = assignToCells(remaining, cellsToFill);

  chosen.forEach(({ item, cell }) => {
    const { sticker } = item;
    sticker.hidden = false;
    const jitterX = (roll(sticker, 'jx') - 0.5) * 0.7;
    const jitterY = (roll(sticker, 'jy') - 0.5) * 0.7;

    const w = sticker.offsetWidth || 76;
    const h = sticker.offsetHeight || 76;

    // Aim at the centre of the cell, then pull the box back inside the page.
    let x = ((cell.c + 0.5 + jitterX) / COLS) * pageWidth - w / 2;
    let y = ((cell.r + 0.5 + jitterY) / ROWS) * pageHeight - h / 2;
    x = Math.max(EDGE, Math.min(pageWidth - w - EDGE, x));
    y = Math.max(EDGE, Math.min(pageHeight - h - EDGE, y));

    if (overlaps(x, y, w, h)) {
      // Push to whichever side of the content it is already nearest, and only
      // if the sticker actually fits there. Trying every side in turn rather
      // than guessing keeps it out from under the panel on any page shape.
      const options = [
        { x: block.left - w - 4, y },                       // left of it
        { x: block.right + 4, y },                          // right of it
        { x, y: block.top - h - 4 },                        // above it
        { x, y: block.bottom + 4 },                         // below it
      ].filter((o) =>
        o.x >= EDGE && o.x + w <= pageWidth - EDGE &&
        o.y >= EDGE && o.y + h <= pageHeight - EDGE);

      if (options.length === 0) { sticker.hidden = true; return; }
      // Nearest to where it wanted to be, so the scatter keeps its shape.
      options.sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y));
      ({ x, y } = options[0]);
    }

    sticker.style.left = `${((x / pageWidth) * 100).toFixed(2)}%`;
    sticker.style.top = `${((y / pageHeight) * 100).toFixed(2)}%`;
    sticker.style.right = '';
  });
}

/** Pop each sticker in as it comes into view, then let it bob. */
export function observeStickers(layer) {
  const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const stickers = [...layer.querySelectorAll('.sticker')];

  if (reduced || typeof IntersectionObserver === 'undefined') {
    for (const sticker of stickers) sticker.classList.add('sticker--in');
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sticker--in');
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px' },
  );
  for (const sticker of stickers) observer.observe(sticker);

  // Gentle parallax: each sticker lags the page by its own depth.
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = globalThis.scrollY ?? 0;
      for (const sticker of stickers) {
        sticker.style.setProperty('--drift', `${y * Number(sticker.dataset.depth) * -0.12}px`);
      }
      ticking = false;
    });
  };
  globalThis.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  return () => {
    observer.disconnect();
    globalThis.removeEventListener('scroll', onScroll);
  };
}
